import { useState, useEffect } from "react";
import { Button, CloseButton, Dialog, Portal, Image, Text, Box, HStack, VStack, Spinner } from "@chakra-ui/react";
import { MdArrowBackIosNew, MdArrowForwardIos } from "react-icons/md";
import { IoOpenOutline } from "react-icons/io5";
import { FaRegFilePdf } from "react-icons/fa6";

export const MultiImageDialog = ({ isOpen, onClose, images = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const currentImage = images[currentIndex];

  // Fetch PDF as blob to bypass X-Frame-Options restrictions
  useEffect(() => {
    // Revoke previous blob URL to prevent memory leaks
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }

    if (!currentImage?.pdf || !currentImage?.fileUrl) return;

    let cancelled = false;
    setPdfLoading(true);

    fetch(currentImage.fileUrl)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch PDF");
        return res.blob();
      })
      .then((blob) => {
        if (!cancelled) {
          const url = URL.createObjectURL(blob);
          setPdfBlobUrl(url);
          setPdfLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setPdfLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentImage?.fileUrl]);

  // Cleanup blob URL on close
  useEffect(() => {
    if (!isOpen && pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  }, [isOpen]);

  const prevImage = () => setCurrentIndex((idx) => (idx === 0 ? images.length - 1 : idx - 1));
  const nextImage = () => setCurrentIndex((idx) => (idx === images.length - 1 ? 0 : idx + 1));

  const handleClick = () => {
    setCurrentIndex(0);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    onClose();
  };

  return (
    <Dialog.Root open={isOpen} size="cover" placement="center" motionPreset="slide-in-bottom">
      <Portal>
        <Dialog.Backdrop bg="blackAlpha.800" backdropFilter="blur(8px)" />
        <Dialog.Positioner>
          <Dialog.Content
            maxW="900px"
            width="90vw"
            maxH="85vh"
            borderRadius="16px"
            bg="white"
            display="flex"
            flexDirection="column"
            overflow="hidden"
            boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
          >
            {/* Header */}
            <Dialog.Header
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              px={6}
              py={4}
              bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              color="white"
            >
              <VStack align="flex-start" spacing={0}>
                <Dialog.Title fontSize="lg" fontWeight="600">
                  KYC Documents
                </Dialog.Title>
                {currentImage?.name && (
                  <Text fontSize="sm" opacity={0.9} fontWeight="400">
                    {currentImage.name}
                  </Text>
                )}
              </VStack>
              <HStack spacing={3}>
                <Text fontSize="sm" opacity={0.9}>
                  {currentIndex + 1} / {images.length}
                </Text>
                <Dialog.CloseTrigger asChild onClick={handleClick}>
                  <CloseButton size="sm" color="white" _hover={{ bg: "whiteAlpha.300" }} />
                </Dialog.CloseTrigger>
              </HStack>
            </Dialog.Header>

            {/* Main Content Area */}
            <Dialog.Body
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="relative"
              bg="gray.50"
              p={6}
              minH="400px"
            >
              {/* Navigation Arrows */}
              {images.length > 1 && (
                <>
                  <Box
                    as="button"
                    onClick={prevImage}
                    position="absolute"
                    left={4}
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex={1}
                    cursor="pointer"
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    bg="white"
                    boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    transition="all 0.2s"
                    _hover={{ bg: "gray.50", boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
                  >
                    <MdArrowBackIosNew size="16px" color="#4a5568" />
                  </Box>
                  <Box
                    as="button"
                    onClick={nextImage}
                    position="absolute"
                    right={4}
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex={1}
                    cursor="pointer"
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    bg="white"
                    boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    transition="all 0.2s"
                    _hover={{ bg: "gray.50", boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
                  >
                    <MdArrowForwardIos size="16px" color="#4a5568" />
                  </Box>
                </>
              )}

              {/* Image/PDF Display */}
              {currentImage?.pdf ? (
                <VStack spacing={4} w="100%" maxW="800px">
                  <Box
                    w="100%"
                    h="55vh"
                    borderRadius="8px"
                    overflow="hidden"
                    border="1px solid"
                    borderColor="gray.200"
                    boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1)"
                    position="relative"
                    bg="white"
                  >
                    {pdfLoading ? (
                      <Box
                        position="absolute"
                        top="50%"
                        left="50%"
                        transform="translate(-50%, -50%)"
                      >
                        <Spinner size="lg" color="purple.500" />
                      </Box>
                    ) : pdfBlobUrl ? (
                      <iframe
                        src={pdfBlobUrl}
                        title={currentImage?.name || "PDF Document"}
                        width="100%"
                        height="100%"
                        style={{ border: "none" }}
                      />
                    ) : (
                      <Box
                        position="absolute"
                        top="50%"
                        left="50%"
                        transform="translate(-50%, -50%)"
                      >
                        <Text color="gray.500">Failed to load PDF preview</Text>
                      </Box>
                    )}
                  </Box>
                  <Button
                    variant="surface"
                    colorPalette="blue"
                    size="md"
                    onClick={() => window.open(currentImage?.fileUrl, "_blank")}
                    borderRadius="8px"
                  >
                    <IoOpenOutline />
                    Open PDF in New Tab
                  </Button>
                </VStack>
              ) : (
                <Image
                  src={currentImage?.fileUrl}
                  alt={currentImage?.name || `Document ${currentIndex + 1}`}
                  maxHeight="55vh"
                  maxWidth="100%"
                  objectFit="contain"
                  borderRadius="8px"
                  boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                />
              )}
            </Dialog.Body>

            {/* Thumbnail Strip */}
            {images.length > 1 && (
              <Box
                px={6}
                py={4}
                bg="white"
                borderTop="1px solid"
                borderColor="gray.100"
              >
                <HStack spacing={3} justifyContent="center" overflowX="auto">
                  {images.map((img, idx) => (
                    <Box
                      key={idx}
                      as="button"
                      onClick={() => setCurrentIndex(idx)}
                      position="relative"
                      w="64px"
                      h="64px"
                      borderRadius="8px"
                      overflow="hidden"
                      border={idx === currentIndex ? "2px solid" : "2px solid transparent"}
                      borderColor={idx === currentIndex ? "purple.500" : "gray.200"}
                      opacity={idx === currentIndex ? 1 : 0.6}
                      transition="all 0.2s"
                      _hover={{ opacity: 1, borderColor: "purple.300" }}
                      flexShrink={0}
                    >
                      {img.pdf ? (
                        <Box
                          w="100%"
                          h="100%"
                          bg="linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <FaRegFilePdf size="20px" color="#dc2626" />
                        </Box>
                      ) : (
                        <Image
                          src={img.fileUrl}
                          alt={img.name || `Thumbnail ${idx + 1}`}
                          w="100%"
                          h="100%"
                          objectFit="cover"
                        />
                      )}
                    </Box>
                  ))}
                </HStack>
              </Box>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
